/**
 * 맵 좌표에서 CCTV 좌/우 이웃 체인을 도출한다.
 *
 * offline-manage 의 internal/cctvrel/generate.go 를 그대로 옮긴 것 — 두 콘솔이
 * 같은 맵에서 서로 다른 관계를 만들면 현장에서 어느 쪽이 맞는지 알 수 없다.
 *
 * 이름(…_통로_순번)으로 묶지 않는다: 같은 통로 번호를 달고도 맵에서 멀리 떨어진
 * 카메라가 있어(6_15 ↔ 6_16 이 맵 반대편) 이름 순 체인이 엉뚱한 곳을 이었다.
 * 순수하게 좌표만 본다.
 *
 *  1. 최근접거리 중앙값으로 "이 층에서 이웃이라 부를 만한 거리" 를 정한다 —
 *     층마다 카메라 밀도가 달라도 같은 기준이 통한다.
 *  2. 가까운 쌍부터 채택하되 양쪽 모두 그 방위가 비어 있을 때만 잇는다. 카메라별로
 *     따로 고르면 반대편에서 추가한 쌍이 같은 직선을 다시 이어 한 홉에 두 칸씩
 *     번진다(실측 149건).
 *  3. 좌우는 좌표로 정한다: 가로로 벌어진 쌍은 x 가 작은 쪽이 왼쪽, 세로 통로처럼
 *     x 차이가 거의 없으면 y 가 작은 쪽을 왼쪽으로 본다.
 *
 * depth 는 항상 1 이다 — 2·3칸 건너뛴 행을 넣으면 그것들도 "바로 옆" 으로 해석돼
 * 점등이 훨씬 멀리 번진다.
 */

import type {
  CctvConnection,
  GeneratedRelations,
  RelationCamera,
  RelationObstacle,
} from '../model/types'

/** 최근접거리 중앙값의 몇 배까지 이웃으로 보나. */
const MAX_LINK = 2.5

/** 두 이웃의 방위가 이 각도(50°) 안이면 "같은 갈래" 로 보고 가까운 쪽만 남긴다. */
const SAME_DIR_COS = Math.cos((50 * Math.PI) / 180)

function dist(a: RelationCamera, b: RelationCamera): number {
  return Math.hypot(a.x - b.x, a.y - b.y)
}

export function generateRelations(
  cams: RelationCamera[],
  obstacles: RelationObstacle[] = [],
): GeneratedRelations {
  // 이름이 비었거나 중복인 카메라는 버린다 — 같은 이름이 두 번 찍힌 맵이 있어
  // (P1_B1_3_21) 두 좌표를 다 쓰면 한 이름에 모순되는 이웃이 생긴다.
  const list: RelationCamera[] = []
  const seen = new Set<string>()
  for (const c of cams) {
    if (!c.name || seen.has(c.name)) continue
    seen.add(c.name)
    list.push(c)
  }
  const out: GeneratedRelations = { rows: [], lonely: 0 }
  if (list.length < 2) return out

  // 최근접거리 중앙값.
  const nearest: number[] = []
  for (let i = 0; i < list.length; i++) {
    let best = Infinity
    for (let j = 0; j < list.length; j++) {
      if (i !== j) best = Math.min(best, dist(list[i], list[j]))
    }
    if (Number.isFinite(best)) nearest.push(best)
  }
  nearest.sort((a, b) => a - b)
  const med = nearest.length > 0 ? nearest[Math.floor(nearest.length / 2)] : 0
  const limit = med > 0 ? med * MAX_LINK : Infinity

  // 후보 쌍 — 거리 안에 든 모든 조합(같은 쌍은 한 번만).
  const cand: { a: number; b: number; len: number }[] = []
  for (let i = 0; i < list.length; i++) {
    for (let j = i + 1; j < list.length; j++) {
      const d = dist(list[i], list[j])
      if (d > limit) continue
      if (segmentBlocked(list[i], list[j], obstacles)) continue
      cand.push({ a: i, b: j, len: d })
    }
  }
  // 가까운 쌍부터. 같은 거리면 후보를 만든 순서(카메라 순서)를 유지한다.
  cand.sort((x, y) => x.len - y.len)

  const dirs: [number, number][][] = list.map(() => [])
  const free = (from: number, to: number, length: number): boolean => {
    const ux = (list[to].x - list[from].x) / length
    const uy = (list[to].y - list[from].y) / length
    return !dirs[from].some(([tx, ty]) => tx * ux + ty * uy >= SAME_DIR_COS)
  }
  const claim = (from: number, to: number, length: number): void => {
    dirs[from].push([(list[to].x - list[from].x) / length, (list[to].y - list[from].y) / length])
  }

  const linked = new Set<number>()
  for (const p of cand) {
    if (!free(p.a, p.b, p.len) || !free(p.b, p.a, p.len)) continue
    claim(p.a, p.b, p.len)
    claim(p.b, p.a, p.len)

    linked.add(p.a)
    linked.add(p.b)
    out.rows.push(...pairRows(list[p.a], list[p.b]))
  }
  out.lonely = list.length - linked.size
  return out
}

/**
 * 두 카메라를 잇는 양방향 두 행. 좌우는 좌표로 정한다: 가로로 벌어진 쌍은 x 가
 * 작은 쪽이 왼쪽, 세로 통로처럼 x 차이가 거의 없으면 y 가 작은 쪽이 왼쪽.
 * 자동 생성과 맵에서 손으로 잇는 연결이 같은 규칙을 쓴다(offline-manage
 * cctvrel.js addPreviewPair 와 동일).
 */
export function pairRows(a: RelationCamera, b: RelationCamera): CctvConnection[] {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const aIsLeft = Math.abs(dx) >= Math.abs(dy) ? dx > 0 : dy > 0
  const l = aIsLeft ? a : b
  const r = aIsLeft ? b : a
  return [
    { name: r.name, to: l.name, direction: 'left', depth: 1 },
    { name: l.name, to: r.name, direction: 'right', depth: 1 },
  ]
}

/**
 * 선분 위 몇 점을 폴리곤 내부 판정에 쓰나. 양 끝 15% 는 빼고 본다 — 주차면
 * 위에 얹힌 카메라가 자기 주차면 때문에 전부 끊기면 안 된다.
 */
const BLOCK_SAMPLES = 15

/**
 * a→b 선분이 주차면 폴리곤을 관통하는가. 판정 규칙은 offline-manage 의
 * cctvrel/generate.go segmentBlocked 와 반드시 동일해야 한다.
 */
function segmentBlocked(a: RelationCamera, b: RelationCamera, obstacles: RelationObstacle[]): boolean {
  if (obstacles.length === 0) return false
  const minX = Math.min(a.x, b.x)
  const maxX = Math.max(a.x, b.x)
  const minY = Math.min(a.y, b.y)
  const maxY = Math.max(a.y, b.y)
  for (const poly of obstacles) {
    if (poly.length < 3) continue
    // 폴리곤 bbox 가 선분 bbox 와 겹칠 때만 표본 검사 — 300대 × 수천 스톨.
    let pMinX = Infinity
    let pMaxX = -Infinity
    let pMinY = Infinity
    let pMaxY = -Infinity
    for (const pt of poly) {
      pMinX = Math.min(pMinX, pt.x)
      pMaxX = Math.max(pMaxX, pt.x)
      pMinY = Math.min(pMinY, pt.y)
      pMaxY = Math.max(pMaxY, pt.y)
    }
    if (pMaxX < minX || pMinX > maxX || pMaxY < minY || pMinY > maxY) continue
    for (let i = 0; i < BLOCK_SAMPLES; i++) {
      const t = 0.15 + (0.7 * (i + 0.5)) / BLOCK_SAMPLES
      if (pointInPolygon(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t, poly)) return true
    }
  }
  return false
}

/** 표준 even-odd 레이 캐스트. */
function pointInPolygon(x: number, y: number, poly: RelationObstacle): boolean {
  let inside = false
  let j = poly.length - 1
  for (let i = 0; i < poly.length; i++) {
    const { x: xi, y: yi } = poly[i]
    const { x: xj, y: yj } = poly[j]
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside
    j = i
  }
  return inside
}

/** 한 CCTV 의 행만 골라 방향·이름 순으로 정렬 — 편집 표에 그대로 쓴다. */
export function rowsFor(rows: CctvConnection[], name: string): CctvConnection[] {
  return rows
    .filter((r) => r.name === name)
    .sort((a, b) => a.direction.localeCompare(b.direction) || a.to.localeCompare(b.to))
}
